"use client";

import { useEffect, useRef, useState } from "react";
import If from "./If";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import useIntersectionObserver from "@/hooks/useIntersectionObserver";

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

export default function Image({
  src = "",
  alt = "",
  maxWidth = "",
  shouldLazyLoad = true,
  objectPosition = "center",
  cover = true,
  width = "",
  height = "",
  className = "",
  imgClassName = "",
  fillContainer = false,
  imgStyle = {},
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);
  const backgroundEle = useRef(null);
  useGSAP(
    () => {
      if (!gsap || !backgroundEle.current || typeof window === "undefined") {
        return;
      }

      const tl = gsap.timeline({
        paused: true,
        scrollTrigger: {
          trigger: backgroundEle.current,
          start: "top bottom-=15%",
          end: `bottom bottom`,
          scrub: 0.5,
        },
      });

      tl.to(backgroundEle.current, {
        // clipPath: "inset(0% 0% 0% 0% round 0)",
        opacity: 1,
        transform: "translateY(0) scale(1)",
        ease: "none",
        onEnter: () => {},
        onEnterBack: () => {},
        onComplete: () => {
          // backgroundEle.current.style.clipPath = "inset(0 0 0 0 round 0)";
        },
      });
    },
    {
      dependencies: [backgroundEle.current],
    }
  );

  useEffect(() => {
    const img = imgRef.current;

    if (!img) return;

    const handleLoad = () => {
      setLoaded(true);
    };

    if (img.complete) {
      handleLoad();
    } else {
      img.addEventListener("load", handleLoad);
    }

    return () => {
      img.removeEventListener("load", handleLoad);
    };
  }, [src]);

  const commonImgProps = {
    ref: imgRef,
    width: width,
    height: height,
    alt: alt,
    loading: shouldLazyLoad ? "lazy" : "eager",
    decoding: "async",
    fetchPriority: shouldLazyLoad ? "low" : "high",
    className: `${objectPosition === "center" ? "object-center" : ""}
      ${cover ? "object-cover" : "object-contain"}
      ${imgClassName}
      ${fillContainer ? "h-full w-full" : ""}
      transition-opacity duration-300 ease-in
      ${loaded ? "opacity-100" : "opacity-0"}`,
    style: imgStyle,
  };

  const renderImage = (srcWithParams) => (
    <img {...commonImgProps} src={srcWithParams} />
  );

  return (
    <picture
      className={`${className} ${fillContainer ? "h-full w-full" : ""}`}
      {...props}
      ref={backgroundEle}
      style={{
        // clipPath: "inset(5% 5% 5% 5% round 2rem)",
        transform: "translateY(100px) scale(0.95)",
        opacity: 0,
        transformOrigin: "center",
      }}
    >
      <If condition={!src?.includes("bfldr.com")}>
        <source
          type="image/avif"
          media="(max-width: 500px)"
          srcSet={`${src}?fm=avif&w=1000`}
        />
        <source
          type="image/avif"
          media="(max-width: 1200px)"
          srcSet={`${src}?fm=avif&w=1200`}
        />
        <source
          type="image/avif"
          media="(min-width: 1201px)"
          srcSet={`${src}?fm=avif${maxWidth ? `&w=${maxWidth}` : ""}`}
        />
        <source
          type="image/webp"
          media="(max-width: 500px)"
          srcSet={`${src}?fm=webp&w=1000`}
        />
        <source
          type="image/webp"
          media="(max-width: 1200px)"
          srcSet={`${src}?fm=webp&w=1200`}
        />
        <source
          type="image/webp"
          media="(min-width: 1201px)"
          srcSet={`${src}?fm=webp${maxWidth ? `&w=${maxWidth}` : ""}`}
        />
        <source
          type="image/jpeg"
          media="(max-width: 500px)"
          srcSet={`${src}?fm=jpg&w=1000`}
        />
        <source
          type="image/jpeg"
          media="(max-width: 1200px)"
          srcSet={`${src}?fm=jpg&w=1200`}
        />
        <source
          type="image/jpeg"
          media="(min-width: 1201px)"
          srcSet={`${src}?fm=jpg${maxWidth ? `&w=${maxWidth}` : ""}`}
        />
        {renderImage(`${src}${maxWidth ? `?w=${maxWidth}` : ""}`)}
      </If>

      <If condition={src?.includes("bfldr.com")}>
        <source
          type="image/webp"
          media="(max-width: 500px)"
          srcSet={`${src}&width=1000`}
        />
        <source
          type="image/webp"
          media="(max-width: 1200px)"
          srcSet={`${src}&width=1200`}
        />
        <source
          type="image/webp"
          media="(min-width: 1201px)"
          srcSet={`${src}${maxWidth ? `&width=${maxWidth}` : ""}`}
        />
        <source
          type="image/jpeg"
          media="(max-width: 500px)"
          srcSet={`${src}&width=1000`}
        />
        <source
          type="image/jpeg"
          media="(max-width: 1200px)"
          srcSet={`${src}&width=1200`}
        />
        <source
          type="image/jpeg"
          media="(min-width: 1201px)"
          srcSet={`${src}${maxWidth ? `&width=${maxWidth}` : ""}`}
        />
        {renderImage(`${src}${maxWidth ? `&width=${maxWidth}` : ""}`)}
      </If>
    </picture>
  );
}
